import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  WorkflowActionCatalogApiItem,
  WorkflowApiConnectorCatalogItem,
  WorkflowModuleOperationCatalogItem,
  WorkflowTriggerSubscriptionItem,
  WorkflowModuleSurfaceCatalogItem
} from './workflow-catalog.models';
import { WorkflowTaskOperations } from '../../shared/workflow-surfaces/workflow-task-operations';

const DEFAULT_BPMN_XML = `<?xml version="1.0" encoding="UTF-8"?>
<bpmn:definitions xmlns:bpmn="http://www.omg.org/spec/BPMN/20100524/MODEL"
                  xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI"
                  xmlns:dc="http://www.omg.org/spec/DD/20100524/DC"
                  xmlns:di="http://www.omg.org/spec/DD/20100524/DI"
                  id="Definitions_Default"
                  targetNamespace="http://ain.local/workflow">
  <bpmn:process id="Process_Default" isExecutable="true">
    <bpmn:startEvent id="startEvent_1" name="Start" />
    <bpmn:userTask id="task_approval" name="Approval Task" />
    <bpmn:endEvent id="endEvent_1" name="End" />
    <bpmn:sequenceFlow id="flow_start_to_task" sourceRef="startEvent_1" targetRef="task_approval" />
    <bpmn:sequenceFlow id="flow_task_to_end" sourceRef="task_approval" targetRef="endEvent_1" />
  </bpmn:process>
  <bpmndi:BPMNDiagram id="BPMNDiagram_Default">
    <bpmndi:BPMNPlane id="BPMNPlane_Default" bpmnElement="Process_Default">
      <bpmndi:BPMNShape id="shape_startEvent_1" bpmnElement="startEvent_1">
        <dc:Bounds x="160" y="100" width="36" height="36" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="shape_task_approval" bpmnElement="task_approval">
        <dc:Bounds x="260" y="78" width="120" height="80" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNShape id="shape_endEvent_1" bpmnElement="endEvent_1">
        <dc:Bounds x="450" y="100" width="36" height="36" />
      </bpmndi:BPMNShape>
      <bpmndi:BPMNEdge id="edge_flow_start_to_task" bpmnElement="flow_start_to_task">
        <di:waypoint x="196" y="118" />
        <di:waypoint x="260" y="118" />
      </bpmndi:BPMNEdge>
      <bpmndi:BPMNEdge id="edge_flow_task_to_end" bpmnElement="flow_task_to_end">
        <di:waypoint x="380" y="118" />
        <di:waypoint x="450" y="118" />
      </bpmndi:BPMNEdge>
    </bpmndi:BPMNPlane>
  </bpmndi:BPMNDiagram>
</bpmn:definitions>`;

const DEFAULT_PARSED_GRAPH_SNAPSHOT = JSON.stringify({
  designVersion: 1,
  bpmnXmlHash: '',
  startTriggers: [
    {
      id: 'init-trigger',
      name: 'Manual Start',
      type: 'Manual',
      configurationJson: '{}'
    }
  ],
  operators: [
    {
      id: 'init-op',
      name: 'Approval Task',
      type: 'UserTask',
      bpmnElementId: 'task_approval',
      configurationJson: '{}'
    }
  ]
});

export interface ProcessDefinition {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  status: string;
  currentVersion: number;
  isActive: boolean;
}

export interface ProcessDefinitionDetail extends ProcessDefinition {
  description?: string;
  category?: string;
  startType: string;
  resultType: string;
  latestBpmnXml: string;
  latestParsedGraphSnapshot: string;
  latestNotes: string;
  versions: ProcessVersion[];
}

export interface ProcessVersion {
  id: string;
  processDefinitionId: string;
  version: number;
  status: string;
  isDefault: boolean;
}

export interface ProcessVersionDetail extends ProcessVersion {
  bpmnXml: string;
  parsedGraphSnapshot: string;
  notes: string;
  createdAtUtc: string;
  publishedAtUtc?: string;
}

export interface ProcessVersionCanonicalGraph {
  processDefinitionId: string;
  processVersionId: string;
  elements: WorkflowProcessElement[];
  flows: WorkflowProcessFlow[];
  variableDefinitions: WorkflowProcessVariableDefinition[];
  variableMappings: WorkflowProcessVariableMapping[];
  resourceBindings: WorkflowProcessResourceBinding[];
}

export interface WorkflowProcessElement {
  bpmnElementId: string;
  elementType: string;
  name: string;
  configurationJson: string;
  metadataJson: string;
  isActive: boolean;
}

export interface WorkflowProcessFlow {
  bpmnSequenceFlowId: string;
  sourceElementId: string;
  targetElementId: string;
  conditionType: string;
  conditionExpression: string;
  isDefault: boolean;
  priority: number;
  metadataJson: string;
}

export interface WorkflowProcessVariableDefinition {
  name: string;
  type: string;
  scope: string;
  defaultValue: string;
  isRequired: boolean;
  isReadOnly: boolean;
  validationRulesJson: string;
  maskingPolicy: string;
}

export interface WorkflowProcessVariableMapping {
  variableName: string;
  targetElementId: string;
  mappingDirection: string;
  sourcePath: string;
  targetPath: string;
  transformationJson: string;
  priority: number;
  isActive: boolean;
}

export interface WorkflowProcessResourceBinding {
  bpmnElementId: string;
  bindingScope: string;
  bindingType: string;
  resourceKey: string;
  resourceType: string;
  moduleKey: string;
  referenceId: string;
  configurationJson: string;
  metadataJson: string;
  isRequired: boolean;
  isActive: boolean;
}

export interface ProcessInstance {
  id: string;
  processDefinitionId: string;
  status: string;
  referenceNumber?: string;
  businessKey?: string;
  startDateUtc: string;
}

export interface ProcessInstanceDetail extends ProcessInstance {
  tenantId: string;
  currentElementId: string;
  initiatorUserId: string;
  variablesJson?: string;
}

export interface ProcessInstanceExecution {
  id: string;
  bpmnElementId: string;
  elementType: string;
  status: string;
  sequenceNumber: number;
  startedAtUtc?: string;
  completedAtUtc?: string;
}

export interface ProcessInstanceSummary {
  id: string;
  referenceNumber?: string;
  businessKey?: string;
  status: string;
  boundModuleKey?: string;
  boundEntityName?: string;
  boundEntityId?: string;
  variablesJson: string;
}

export interface ProcessTask {
  id: string;
  tenantId: string;
  processInstanceId: string;
  name: string;
  status: string;
  assignedUserId?: string;
  bpmnElementId: string;
  operatorType?: string;
  surfaceKey?: string;
  createdAtUtc: string;
  dueDateUtc?: string;
}

export interface ProcessTaskDetail extends ProcessTask {
  organizationUnitId?: string;
  description: string;
  priority: number;
  dueDateUtc?: string;
  inputDataJson: string;
  outputDataJson: string;
  formDataJson: string;
  formDefinitionId?: string;
  boundModuleKey?: string;
  boundEntityName?: string;
  boundEntityId?: string;
  variablesJson: string;
  moduleKey?: string;
  surfaceKey?: string;
  surfaceConfigurationJson?: string;
  processInstance?: ProcessInstanceSummary;
}

export interface HookBinding {
  id: string;
  tenantId: string;
  moduleKey: string;
  entityName: string;
  actionKey: string;
  processDefinitionId?: string;
  fallbackBehavior: string;
  isActive: boolean;
}

export interface WorkflowHookRegistryItem {
  moduleKey: string;
  entityType: string;
  action: string;
  triggerEvent: string;
  payloadSchemaKey: string;
  defaultBehaviorWithoutWorkflow: string;
  supportedOutcomes: string[];
}

export interface CreateHookBindingRequest {
  moduleKey: string;
  entityName: string;
  actionKey: string;
  processDefinitionId: string | null;
  processDefinitionVersionId: string | null;
  fallbackBehavior: string;
  isActive: boolean;
  configuration: string | null;
}

export interface UpdateHookBindingRequest {
  processDefinitionId: string | null;
  processDefinitionVersionId: string | null;
  fallbackBehavior: string;
  isActive: boolean;
  configuration: string | null;
}

export interface WorkflowIntentOpsListItem {
  intentRequestId: string;
  planId: string;
  status: string;
  approvalStatus: string;
  executionStatus: string;
  channel: string;
  actorUserId: string;
  plannedAtUtc: string;
  confirmedAtUtc?: string;
  approvedAtUtc?: string;
  rejectedAtUtc?: string;
  processInstanceId?: string;
  retryCount: number;
  lastExecutionErrorCode?: string;
  lastExecutionErrorMessage?: string;
  intentKey: string;
  riskLevel: string;
  safetyDecision: string;
}

export interface WorkflowIntentTimelineEvent {
  id: string;
  eventType: string;
  actorUserId?: string;
  occurredAtUtc: string;
  status: string;
  approvalStatus: string;
  executionStatus: string;
  code?: string;
  messageKey?: string;
  correlationId?: string;
  idempotencyKey?: string;
  metadataJson: string;
}

export interface WorkflowIntentApprovalTask {
  intentRequestId: string;
  status: string;
  assignedUserId?: string;
  assignedRoleKey?: string;
  assignedOrganizationUnitId?: string;
  claimedByUserId?: string;
  claimedAtUtc?: string;
  dueAtUtc?: string;
  slaMinutes?: number;
  completedByUserId?: string;
  completedAtUtc?: string;
}

export interface WorkflowIntentOpsDetail {
  intent: WorkflowIntentPlan;
  timeline: WorkflowIntentTimelineEvent[];
  approvalTask?: WorkflowIntentApprovalTask;
  eligibleActions: string[];
}

export interface WorkflowIntentPlan {
  intentRequestId: string;
  planId: string;
  envelope: {
    tenantId: string;
    organizationUnitId?: string;
    actorUserId: string;
    channel: string;
    locale: string;
    text: string;
    conversationId?: string;
    correlationId?: string;
    contextJson: string;
    attachmentsJson: string;
    receivedAtUtc: string;
  };
  draftPlan: {
    planId: string;
    targetType: string;
    targetKey?: string;
    variablesJson: string;
    missingInputsJson: string;
    requiresClarification: boolean;
    requiresApproval: boolean;
    explanationJson: string;
    riskJson: string;
    intentKey: string;
    confidence: number;
    slotsJson: string;
    missingSlotsJson: string;
    riskLevel: string;
    evidenceJson: string;
    resolutionJson: string;
    selectedCandidateJson: string;
    candidatesJson: string;
    safety: {
      decision: string;
      reasonCode: string;
      requiresClarification: boolean;
      requiresApproval: boolean;
      isExecutionReady: boolean;
      isPermissionGranted: boolean;
      sourceType?: string;
    };
  };
  status: string;
  confirmationStatus: string;
  confirmedAtUtc?: string;
  confirmedByUserId?: string;
  approvalStatus: string;
  executionStatus: string;
  processInstanceId?: string;
  plannedAtUtc: string;
}

export interface WorkflowIntentOpsActionResponse {
  intent: WorkflowIntentPlan;
  replayIntentRequestId?: string;
}

export interface WorkflowIntentEvaluationSample {
  id: string;
  datasetKey: string;
  sampleKey: string;
  inputText: string;
  channel: string;
  locale: string;
  contextJson: string;
  expectedIntentKey: string;
  expectedSlotsJson: string;
  expectedCandidateSourceType?: string;
  expectedCandidateTargetKey?: string;
  expectedSafetyDecision?: string;
  isActive: boolean;
  createdAtUtc: string;
  updatedAtUtc?: string;
}

export interface WorkflowIntentEvaluationResult {
  id: string;
  evaluationSampleId: string;
  actualIntentKey: string;
  actualSlotsJson: string;
  actualCandidateSourceType?: string;
  actualCandidateTargetKey?: string;
  actualSafetyDecision: string;
  isIntentMatch: boolean;
  slotFillCompleteness: number;
  isCandidateHit: boolean;
  isSafetyMatch: boolean;
  requiresClarification: boolean;
  requiresApproval: boolean;
  isExecutionReady: boolean;
  mismatchFlagsJson: string;
  comparisonJson: string;
}

export interface WorkflowIntentEvaluationMetrics {
  evaluationRunId: string;
  windowFromUtc?: string;
  windowToUtc?: string;
  totalSamples: number;
  intentMatchedCount: number;
  candidateHitCount: number;
  safetyMismatchCount: number;
  classificationAccuracyProxy: number;
  slotFillCompleteness: number;
  candidateSelectionHitRate: number;
  executionSuccessRate: number;
  executionFailureRate: number;
  clarificationRate: number;
  approvalRate: number;
  safetyDistributionJson: string;
}

export interface WorkflowIntentEvaluationSummary {
  totalSamples: number;
  intentMatchedCount: number;
  candidateHitCount: number;
  safetyMismatchCount: number;
  classificationAccuracyProxy: number;
  slotFillCompleteness: number;
  candidateSelectionHitRate: number;
  executionSuccessRate: number;
  executionFailureRate: number;
  clarificationRate: number;
  approvalRate: number;
  safetyDistributionJson: string;
}

export interface WorkflowIntentEvaluationRun {
  id: string;
  datasetKey: string;
  status: string;
  startedByUserId: string;
  startedAtUtc: string;
  completedAtUtc?: string;
  windowFromUtc?: string;
  windowToUtc?: string;
  configurationJson: string;
  summaryJson: string;
}

export interface WorkflowIntentEvaluationRunDetail {
  run: WorkflowIntentEvaluationRun;
  metrics?: WorkflowIntentEvaluationMetrics;
  results: WorkflowIntentEvaluationResult[];
}

export interface WorkflowIntentRegistrationIssue {
  code: string;
  sourceType: string;
  key: string;
  message: string;
}

export interface WorkflowIntentDefinitionCatalogItem {
  moduleKey: string;
  intentKey: string;
  displayName: string;
  namespaceKey: string;
  riskLevel: string;
  requiredSlots: string[];
  requiredPermissions: string[];
  requiresApproval: boolean;
  isRuntimeExecutable: boolean;
  executabilityHint: string;
}

export interface WorkflowIntentTemplateCatalogItem {
  intentKey: string;
  templateKey: string;
  moduleKey: string;
  displayName: string;
  version: string;
  status: string;
  compatibilityScope: string;
  isRuntimeCompatible: boolean;
  owner: string;
  publishedAtUtc?: string;
  publishedByUserId?: string;
  deprecatedAtUtc?: string;
  deprecatedByUserId?: string;
  deprecationReason?: string;
}

export interface WorkflowIntentActionCatalogItem {
  intentKey: string;
  moduleKey: string;
  entityName: string;
  actionKey: string;
  displayName: string;
  sourceType: string;
  requiredInputs: string[];
  requiredPermissions: string[];
  requiresApproval: boolean;
  isRuntimeExecutable: boolean;
  executabilityHint: string;
}

export interface WorkflowIntentDiscoveryCatalog {
  intents: WorkflowIntentDefinitionCatalogItem[];
  templates: WorkflowIntentTemplateCatalogItem[];
  hookActions: WorkflowIntentActionCatalogItem[];
  moduleActions: WorkflowIntentActionCatalogItem[];
  issues: WorkflowIntentRegistrationIssue[];
}

export interface WorkflowIntentGovernancePolicyMismatch {
  sourceType: string;
  sourceKey: string;
  permission: string;
  code: string;
  message: string;
}

export interface WorkflowIntentExecutionSourceMapping {
  sourceType: string;
  sourceKey: string;
  processDefinitionId?: string | null;
  status: string;
  lastValidatedAtUtc?: string | null;
  validationCode?: string | null;
  notes?: string | null;
}

export interface WorkflowIntentGovernanceHealth {
  checkedAtUtc: string;
  status: string;
  mismatchCount: number;
  validationIssueCount: number;
  summary: string;
  mismatches: WorkflowIntentGovernancePolicyMismatch[];
  validationIssues: WorkflowIntentRegistrationIssue[];
}

export interface UpsertWorkflowIntentExecutionSourceRequest {
  processDefinitionId?: string | null;
  status?: string | null;
  validationCode?: string | null;
  notes?: string | null;
}

export interface PlanWorkflowIntentRequest {
  text: string;
  channel?: string;
  locale?: string;
  conversationId?: string;
  correlationId?: string;
  contextJson?: string;
  attachmentsJson?: string;
}

export interface PagedApiResponse<T> {
  items: T[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
}

@Injectable({ providedIn: 'root' })
export class WorkflowApiService implements WorkflowTaskOperations {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/workflow`;

  listDefinitions(options?: { silent?: boolean }): Observable<ProcessDefinition[]> {
    return this.http.get<ProcessDefinition[]>(`${this.base}/process-definitions`, {
      params: options?.silent ? { silent: 'true' } : {}
    });
  }

  getDefinition(id: string): Observable<ProcessDefinitionDetail> {
    return this.http.get<ProcessDefinitionDetail>(`${this.base}/process-definitions/${id}`);
  }

  createDefinition(body: {
    name: string;
    description?: string;
    category?: string;
    bpmnXml?: string;
  }): Observable<ProcessDefinition> {
    return this.http.post<ProcessDefinition>(`${this.base}/process-definitions`, {
      name: body.name,
      description: body.description ?? '',
      category: body.category ?? 'general',
      startType: 'manual',
      resultType: 'approval',
      bpmnXml: body.bpmnXml ?? DEFAULT_BPMN_XML,
      parsedGraphSnapshot: DEFAULT_PARSED_GRAPH_SNAPSHOT,
      notes: ''
    });
  }

  addVersion(processDefinitionId: string, bpmnXml: string): Observable<ProcessVersion> {
    return this.http.post<ProcessVersion>(
      `${this.base}/process-definitions/${processDefinitionId}/versions`,
      { bpmnXml, parsedGraphSnapshot: '{}', notes: '' }
    );
  }

  addVersionWithSnapshot(processDefinitionId: string, bpmnXml: string, parsedGraphSnapshot: string, notes: string): Observable<ProcessVersion> {
    return this.http.post<ProcessVersion>(
      `${this.base}/process-definitions/${processDefinitionId}/versions`,
      { bpmnXml, parsedGraphSnapshot, notes }
    );
  }

  getVersion(processDefinitionId: string, versionId: string): Observable<ProcessVersionDetail> {
    return this.http.get<ProcessVersionDetail>(
      `${this.base}/process-definitions/${processDefinitionId}/versions/${versionId}`
    );
  }

  getVersionCanonicalGraph(processDefinitionId: string, versionId: string): Observable<ProcessVersionCanonicalGraph> {
    return this.http.get<ProcessVersionCanonicalGraph>(
      `${this.base}/process-definitions/${processDefinitionId}/versions/${versionId}/canonical-graph`
    );
  }

  updateDefinition(
    processDefinitionId: string,
    payload: {
      name: string;
      description: string;
      category: string;
      startType: string;
      resultType: string;
    }
  ): Observable<ProcessDefinition> {
    return this.http.put<ProcessDefinition>(`${this.base}/process-definitions/${processDefinitionId}`, payload);
  }

  publishDefinition(processDefinitionId: string): Observable<ProcessVersion> {
    return this.http.post<ProcessVersion>(`${this.base}/process-definitions/${processDefinitionId}/publish`, null);
  }

  archiveDefinition(processDefinitionId: string): Observable<void> {
    return this.http.post<void>(`${this.base}/process-definitions/${processDefinitionId}/archive`, null);
  }

  listInstances(): Observable<ProcessInstance[]> {
    return this.http.get<ProcessInstance[]>(`${this.base}/process-instances`);
  }

  getInstance(id: string): Observable<ProcessInstanceDetail> {
    return this.http.get<ProcessInstanceDetail>(`${this.base}/instances/${id}`);
  }

  getInstanceHistory(id: string): Observable<unknown[]> {
    return this.http.get<unknown[]>(`${this.base}/instances/${id}/history`);
  }

  getInstanceTasks(id: string): Observable<ProcessTask[]> {
    return this.http.get<ProcessTask[]>(`${this.base}/instances/${id}/tasks`);
  }

  getInstanceExecutions(id: string): Observable<ProcessInstanceExecution[]> {
    return this.http.get<ProcessInstanceExecution[]>(`${this.base}/instances/${id}/executions`);
  }

  startInstance(processDefinitionId: string, businessKey: string): Observable<ProcessInstanceDetail> {
    return this.http.post<ProcessInstanceDetail>(
      `${this.base}/process-definitions/${processDefinitionId}/instances`,
      { businessKey, referenceNumber: businessKey, variablesJson: '{}' }
    );
  }

  cancelInstance(id: string): Observable<ProcessInstanceDetail> {
    return this.http.post<ProcessInstanceDetail>(`${this.base}/instances/${id}/cancel`, null);
  }

  getInbox(): Observable<ProcessTask[]> {
    return this.http.get<ProcessTask[]>(`${this.base}/tasks/inbox`);
  }

  getTask(id: string): Observable<ProcessTaskDetail> {
    return this.http.get<ProcessTaskDetail>(`${this.base}/tasks/${id}`);
  }

  claimTask(id: string): Observable<ProcessTask> {
    return this.http.post<ProcessTask>(`${this.base}/tasks/${id}/claim`, null);
  }

  saveTaskFormData(id: string, formDataJson: string): Observable<ProcessTask> {
    return this.http.put<ProcessTask>(`${this.base}/tasks/${id}/form-data`, { formDataJson });
  }

  completeTask(id: string, outputDataJson: string): Observable<ProcessTask> {
    return this.http.post<ProcessTask>(`${this.base}/tasks/${id}/complete`, { outputDataJson });
  }

  delegateTask(id: string, toUserId: string, reason: string): Observable<ProcessTask> {
    return this.http.post<ProcessTask>(`${this.base}/tasks/${id}/delegate`, { toUserId, reason });
  }

  escalateTask(id: string, targetUserId?: string, reason?: string): Observable<ProcessTask> {
    return this.http.post<ProcessTask>(`${this.base}/tasks/${id}/escalate`, {
      targetUserId: targetUserId || null,
      reason: reason || null
    });
  }

  addComment(id: string, body: string): Observable<unknown> {
    return this.http.post(`${this.base}/tasks/${id}/comments`, { body });
  }

  listHookBindings(): Observable<HookBinding[]> {
    return this.http.get<HookBinding[]>(`${this.base}/hooks/bindings`);
  }

  listHookRegistry(): Observable<WorkflowHookRegistryItem[]> {
    return this.http.get<WorkflowHookRegistryItem[]>(`${this.base}/hooks/registry`);
  }

  createHookBinding(body: CreateHookBindingRequest): Observable<HookBinding> {
    return this.http.post<HookBinding>(`${this.base}/hooks/bindings`, body);
  }

  updateHookBinding(bindingId: string, body: UpdateHookBindingRequest): Observable<HookBinding> {
    return this.http.put<HookBinding>(`${this.base}/hooks/bindings/${bindingId}`, body);
  }

  getOperationsCatalog(moduleKey?: string): Observable<WorkflowModuleOperationCatalogItem[]> {
    let params = new HttpParams();
    if (moduleKey) {
      params = params.set('moduleKey', moduleKey);
    }
    return this.http.get<WorkflowModuleOperationCatalogItem[]>(`${this.base}/operations/catalog`, { params });
  }

  getConnectorsCatalog(): Observable<WorkflowApiConnectorCatalogItem[]> {
    return this.http.get<WorkflowApiConnectorCatalogItem[]>(`${this.base}/connectors/catalog`);
  }

  getActionsCatalog(): Observable<WorkflowActionCatalogApiItem[]> {
    return this.http.get<WorkflowActionCatalogApiItem[]>(`${this.base}/actions/catalog`);
  }

  getModuleSurfacesCatalog(): Observable<WorkflowModuleSurfaceCatalogItem[]> {
    return this.http.get<WorkflowModuleSurfaceCatalogItem[]>(`${this.base}/module-surfaces/catalog`);
  }

  listTriggerSubscriptions(): Observable<WorkflowTriggerSubscriptionItem[]> {
    return this.http.get<WorkflowTriggerSubscriptionItem[]>(`${this.base}/triggers/subscriptions`);
  }

  listIntentOps(params: {
    status?: string;
    approvalStatus?: string;
    executionStatus?: string;
    channel?: string;
    intentKey?: string;
    pageNumber?: number;
    pageSize?: number;
  }): Observable<PagedApiResponse<WorkflowIntentOpsListItem>> {
    let query = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query = query.set(key, String(value));
      }
    });
    return this.http.get<PagedApiResponse<WorkflowIntentOpsListItem>>(`${this.base}/intents/ops`, { params: query });
  }

  planIntent(request: PlanWorkflowIntentRequest): Observable<WorkflowIntentPlan> {
    return this.http.post<WorkflowIntentPlan>(`${this.base}/intents/plan`, request);
  }

  confirmIntent(intentRequestId: string, request: {
    idempotencyKey: string;
    userConfirmed?: boolean;
    confirmationJson?: string;
  }): Observable<{
    intentRequestId: string;
    status: string;
    processInstanceId?: string;
    executionErrorCode?: string;
  }> {
    return this.http.post<{
      intentRequestId: string;
      status: string;
      processInstanceId?: string;
      executionErrorCode?: string;
    }>(`${this.base}/intents/${intentRequestId}/confirm`, request);
  }

  getIntentOpsDetail(intentRequestId: string): Observable<WorkflowIntentOpsDetail> {
    return this.http.get<WorkflowIntentOpsDetail>(`${this.base}/intents/${intentRequestId}/ops`);
  }

  getIntentApprovalInbox(pageNumber = 1, pageSize = 20): Observable<PagedApiResponse<WorkflowIntentApprovalTask>> {
    return this.queryIntentApprovalInbox({ pageNumber, pageSize });
  }

  queryIntentApprovalInbox(params: {
    status?: string;
    mineOnly?: boolean;
    overdueOnly?: boolean;
    intentKey?: string;
    pageNumber?: number;
    pageSize?: number;
  }): Observable<PagedApiResponse<WorkflowIntentApprovalTask>> {
    let query = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query = query.set(key, String(value));
      }
    });
    return this.http.get<PagedApiResponse<WorkflowIntentApprovalTask>>(`${this.base}/intents/approvals/inbox`, { params: query });
  }

  claimIntentApproval(intentRequestId: string, idempotencyKey: string): Observable<WorkflowIntentApprovalTask> {
    return this.http.post<WorkflowIntentApprovalTask>(`${this.base}/intents/${intentRequestId}/approvals/claim`, { idempotencyKey });
  }

  unclaimIntentApproval(intentRequestId: string, idempotencyKey: string): Observable<WorkflowIntentApprovalTask> {
    return this.http.post<WorkflowIntentApprovalTask>(`${this.base}/intents/${intentRequestId}/approvals/unclaim`, { idempotencyKey });
  }

  getIntentApprovalTimeline(intentRequestId: string): Observable<WorkflowIntentTimelineEvent[]> {
    return this.http.get<WorkflowIntentTimelineEvent[]>(`${this.base}/intents/${intentRequestId}/approvals/timeline`);
  }

  runIntentOpsAction(intentRequestId: string, payload: {
    action: string;
    idempotencyKey?: string;
    reason?: string;
    metadataJson?: string;
    dueAtUtc?: string;
    slaMinutes?: number;
  }): Observable<WorkflowIntentOpsActionResponse> {
    return this.http.post<WorkflowIntentOpsActionResponse>(`${this.base}/intents/${intentRequestId}/ops/actions`, payload);
  }

  upsertIntentEvaluationSample(payload: {
    datasetKey: string;
    sampleKey: string;
    inputText: string;
    channel?: string;
    locale?: string;
    contextJson?: string;
    expectedIntentKey: string;
    expectedSlotsJson?: string;
    expectedCandidateSourceType?: string;
    expectedCandidateTargetKey?: string;
    expectedSafetyDecision?: string;
    isActive?: boolean;
  }): Observable<WorkflowIntentEvaluationSample> {
    return this.http.post<WorkflowIntentEvaluationSample>(`${this.base}/intents/evaluation/samples`, payload);
  }

  listIntentEvaluationSamples(datasetKey?: string, activeOnly = true): Observable<WorkflowIntentEvaluationSample[]> {
    let params = new HttpParams().set('activeOnly', String(activeOnly));
    if (datasetKey) {
      params = params.set('datasetKey', datasetKey);
    }

    return this.http.get<WorkflowIntentEvaluationSample[]>(`${this.base}/intents/evaluation/samples`, { params });
  }

  executeIntentSimulation(payload: {
    datasetKey: string;
    windowFromUtc?: string;
    windowToUtc?: string;
    configurationJson?: string;
  }): Observable<WorkflowIntentEvaluationRunDetail> {
    return this.http.post<WorkflowIntentEvaluationRunDetail>(`${this.base}/intents/evaluation/runs/simulate`, payload);
  }

  listIntentEvaluationRuns(params: {
    datasetKey?: string;
    status?: string;
    pageNumber?: number;
    pageSize?: number;
  }): Observable<PagedApiResponse<WorkflowIntentEvaluationRun>> {
    let query = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query = query.set(key, String(value));
      }
    });

    return this.http.get<PagedApiResponse<WorkflowIntentEvaluationRun>>(`${this.base}/intents/evaluation/runs`, { params: query });
  }

  getIntentEvaluationRun(runId: string): Observable<WorkflowIntentEvaluationRunDetail> {
    return this.http.get<WorkflowIntentEvaluationRunDetail>(`${this.base}/intents/evaluation/runs/${runId}`);
  }

  getIntentEvaluationRunExportUrl(runId: string): string {
    return `${this.base}/intents/evaluation/runs/${runId}/export`;
  }

  getIntentEvaluationMetrics(params: {
    fromUtc?: string;
    toUtc?: string;
    datasetKey?: string;
  }): Observable<WorkflowIntentEvaluationSummary> {
    let query = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        query = query.set(key, String(value));
      }
    });

    return this.http.get<WorkflowIntentEvaluationSummary>(`${this.base}/intents/evaluation/metrics`, { params: query });
  }

  submitIntentFeedback(intentRequestId: string, payload: {
    isIntentCorrect?: boolean;
    correctedIntentKey?: string;
    correctedSlotsJson?: string;
    candidateOverrideSourceType?: string;
    candidateOverrideTargetKey?: string;
    safetyFalsePositive?: boolean;
    safetyFalseNegative?: boolean;
    safetyExpectedDecision?: string;
    datasetKey?: string;
    notes?: string;
  }): Observable<{
    id: string;
    intentRequestId: string;
    submittedByUserId: string;
    submittedAtUtc: string;
    addedToEvaluationDataset: boolean;
  }> {
    return this.http.post<{
      id: string;
      intentRequestId: string;
      submittedByUserId: string;
      submittedAtUtc: string;
      addedToEvaluationDataset: boolean;
    }>(`${this.base}/intents/${intentRequestId}/feedback`, payload);
  }

  getIntentDiscoveryCatalog(moduleKey?: string): Observable<WorkflowIntentDiscoveryCatalog> {
    let params = new HttpParams();
    if (moduleKey) {
      params = params.set('moduleKey', moduleKey);
    }

    return this.http.get<WorkflowIntentDiscoveryCatalog>(`${this.base}/intents/discovery/catalog`, { params });
  }

  getIntentGovernanceHealth(forceRefresh = false, moduleKey?: string): Observable<WorkflowIntentGovernanceHealth> {
    let params = new HttpParams().set('forceRefresh', String(forceRefresh));
    if (moduleKey) {
      params = params.set('moduleKey', moduleKey);
    }
    return this.http.get<WorkflowIntentGovernanceHealth>(`${this.base}/intents/discovery/health`, { params });
  }

  listIntentExecutionSources(params?: {
    sourceType?: string;
    status?: string;
  }): Observable<WorkflowIntentExecutionSourceMapping[]> {
    let query = new HttpParams();
    if (params?.sourceType) {
      query = query.set('sourceType', params.sourceType);
    }
    if (params?.status) {
      query = query.set('status', params.status);
    }

    return this.http.get<WorkflowIntentExecutionSourceMapping[]>(
      `${this.base}/intents/execution-sources`,
      { params: query }
    );
  }

  upsertIntentExecutionSource(
    sourceType: string,
    sourceKey: string,
    body: UpsertWorkflowIntentExecutionSourceRequest
  ): Observable<WorkflowIntentExecutionSourceMapping> {
    const encodedSourceType = encodeURIComponent(sourceType.trim());
    const encodedSourceKey = sourceKey
      .split('/')
      .map(segment => encodeURIComponent(segment))
      .join('/');
    return this.http.put<WorkflowIntentExecutionSourceMapping>(
      `${this.base}/intents/execution-sources/${encodedSourceType}/${encodedSourceKey}`,
      body
    );
  }

  publishIntentTemplate(templateKey: string): Observable<WorkflowIntentTemplateCatalogItem> {
    return this.http.post<WorkflowIntentTemplateCatalogItem>(`${this.base}/intents/discovery/templates/${encodeURIComponent(templateKey)}/publish`, null);
  }

  deprecateIntentTemplate(templateKey: string, reason?: string): Observable<WorkflowIntentTemplateCatalogItem> {
    return this.http.post<WorkflowIntentTemplateCatalogItem>(`${this.base}/intents/discovery/templates/${encodeURIComponent(templateKey)}/deprecate`, {
      reason: reason || undefined
    });
  }
}
